import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { usePageTracking } from "@/lib/analytics";
import ErrorBoundary from "@/components/ErrorBoundary";

import Home from "@/pages/Home";
import MailOutWaterTestingKits from "@/pages/MailOutWaterTestingKits";
import Booking from "@/pages/Booking";
import BookingSuccess from "@/pages/BookingSuccess";
import BookingCancel from "@/pages/BookingCancel";
import KnowledgeHub from "@/pages/KnowledgeHub";
import Article from "@/pages/Article";
import SuburbLanding from "@/pages/SuburbLanding";
import LocationLanding from "@/pages/LocationLanding";
import LocationsHub from "@/pages/LocationsHub";
import AustraliaHub from "@/pages/locations/AustraliaHub";
import StateHub from "@/pages/locations/StateHub";
import RegionHub from "@/pages/locations/RegionHub";
import HierarchicalSuburbLanding from "@/pages/locations/HierarchicalSuburbLanding";
import LocationTopLevelRouter from "@/pages/locations/LocationTopLevelRouter";
import About from "@/pages/About";
import PartnerNetwork from "@/pages/PartnerNetwork";
import PartnerProgram from "@/pages/PartnerProgram";
import RetailCounter from "@/pages/trade/RetailCounter";
import ReferralLanding from "@/pages/ReferralLanding";
import PartnerHub from "@/pages/PartnerHub";
import PartnerProspects from "@/pages/admin/PartnerProspects";
import RetailInvoices from "@/pages/admin/RetailInvoices";
import TradeHub from "@/pages/admin/TradeHub";
import PwsFranchiseeNetwork from "@/pages/PwsFranchiseeNetwork";
import PwsPartnerPresentation from "@/pages/PwsPartnerPresentation";
import PwsPartnerConferenceLanding from "@/pages/PwsPartnerConferenceLanding";
import PwsPartnerConferenceThankYou from "@/pages/PwsPartnerConferenceThankYou";
import PwsPartnerGuideViewer from "@/pages/PwsPartnerGuideViewer";
import BasicWaterSafetyCheck from "@/pages/BasicWaterSafetyCheck";
import EssentialTankWaterTest from "@/pages/EssentialTankWaterTest";
import EssentialBoreWaterTest from "@/pages/EssentialBoreWaterTest";
import BoreWaterTestingLanding from "@/pages/BoreWaterTestingLanding";
import TankWaterHealthCheck from "@/pages/TankWaterHealthCheck";
import TankWaterHealthCheckConfirmed from "@/pages/TankWaterHealthCheckConfirmed";
import TankWaterHealthCheckReferralReceived from "@/pages/TankWaterHealthCheckReferralReceived";
import TankWaterHealthCheckLabOffer from "@/pages/TankWaterHealthCheckLabOffer";
import TankWaterHealthCheckLabConfirmed from "@/pages/TankWaterHealthCheckLabConfirmed";
import HealthCheckActionPlan from "@/pages/HealthCheckActionPlan";
import PropertyManagers from "@/pages/PropertyManagers";
import Contact from "@/pages/Contact";
import BlogIndex from "@/pages/BlogIndex";
import FAQs from "@/pages/FAQs";
import HomeEbookReview from "@/pages/HomeEbookReview";
import FreeGuide from "@/pages/FreeGuide";
import BlogPost from "@/pages/BlogPost";
import AdminBlog from "@/pages/admin/AdminBlog";

// Jun 2026 — legacy per-role login pages replaced by the unified /login
// (see LegacyLoginRedirect below); imports removed with the routes.
import Register from "@/pages/trade/Register";
import Pending from "@/pages/trade/Pending";
import TradeDashboard from "@/pages/trade/Dashboard";
import PlaceOrder from "@/pages/trade/PlaceOrder";
import Orders from "@/pages/trade/Orders";
import UnifiedLogin from "@/pages/UnifiedLogin";
import AuthComplete from "@/pages/AuthComplete";
import AccountSignInMethods from "@/pages/AccountSignInMethods";
import AccountWelcome from "@/pages/AccountWelcome";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";

import AdminUsers from "@/pages/admin/Users";
import InviteAccept from "@/pages/InviteAccept";
import InviteNeighbour from "@/pages/InviteNeighbour";
import AdminBookings from "@/pages/admin/Bookings";
import AdminCarts from "@/pages/admin/Carts";
import Track from "@/pages/Track";
import CustomerRegister from "@/pages/customer/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import CustomerDashboard from "@/pages/customer/Dashboard";
import PassportDashboard from "@/pages/passport/Dashboard";
import PassportProperty from "@/pages/passport/Property";
import PropertyManagerDashboard from "@/pages/PropertyManagerDashboard";
import PmInviteAccept from "@/pages/PmInviteAccept";
import PayForWaterTest from "@/pages/PayForWaterTest";
import LandlordLogin from "@/pages/LandlordLogin";
import LandlordVerify from "@/pages/LandlordVerify";
import LandlordDashboard from "@/pages/LandlordDashboard";
import LandlordWaterHealthEnrol, { LandlordWaterHealthSuccess } from "@/pages/landlord/WaterHealthEnrol";
import LandlordWaterHealthInvitation from "@/pages/landlord/WaterHealthInvitation";
import AdminWaterHealth from "@/pages/admin/AdminWaterHealth";
import AdminPmMarketingResources from "@/pages/admin/AdminPmMarketingResources";
import PwsPartnerMarketingResources from "@/pages/partner/PwsPartnerMarketingResources";
import PmMarketingResources from "@/pages/pm/PmMarketingResources";
import PartnerPropertyManagers from "@/pages/partner/PropertyManagers";
import NotFound from "@/pages/NotFound";
import PropertyManagerTeam from "@/pages/PropertyManagerTeam";
import PropertyManagerInvoices from "@/pages/PropertyManagerInvoices";
import PropertyManagerApply from "@/pages/PropertyManagerApply";
import AdminPropertyManagerApplications from "@/pages/admin/PropertyManagerApplications";
import AdminProperties from "@/pages/admin/Properties";
import PartnerDashboard from "@/pages/partner/Dashboard";
import PartnerTerritory from "@/pages/partner/PartnerTerritory";
import PartnerWelcome from "@/pages/partner/PartnerWelcome";
import PartnerBusinessDetails from "@/pages/partner/BusinessDetails";
import PartnerPaymentDetails from "@/pages/partner/PaymentDetails";
import PartnerQuoteLeads from "@/pages/partner/QuoteLeads";
import PartnerCorrectiveLeads from "@/pages/partner/CorrectiveLeads";
import PartnerMarketingResources from "@/pages/partner/MarketingResources";
import PartnerKitOrders from "@/pages/partner/KitOrders";
import PartnerDocuments from "@/pages/partner/Documents";
import AdminQuoteLeads from "@/pages/admin/QuoteLeads";
import PartnerProfile from "@/pages/partner/Profile";
import PartnerGuide from "@/pages/partner/Guide";
import PartnerLedger from "@/pages/partner/Ledger";
import PartnerBookings from "@/pages/partner/Bookings";
import PartnerCustomers from "@/pages/partner/Customers";
import PartnerCustomerDetail from "@/pages/partner/CustomerDetail";
import PartnerBookingDetail from "@/pages/partner/BookingDetail";
import PartnerPassports from "@/pages/partner/Passports";
import PartnerPassportDetail from "@/pages/partner/PassportDetail";
import PartnerReports from "@/pages/partner/Reports";
import PartnerTestHub from "@/pages/partner/TestHub";
import AdminPropertyDetail from "@/pages/admin/PropertyDetail";
import AdminLab from "@/pages/admin/Lab";
import AdminLabDetail from "@/pages/admin/LabDetail";
import AdminEmails from "@/pages/admin/Emails";
import AdminUnmatchedEmails from "@/pages/admin/UnmatchedEmails";
import AdminReadiness from "@/pages/admin/Readiness";
import AdminStripeLogs from "@/pages/admin/StripeLogs";
import AdminBulkReceive from "@/pages/admin/BulkReceive";
import AdminRecommendations from "@/pages/admin/Recommendations";
import AdminAgriculturalGuidelines from "@/pages/admin/AgriculturalGuidelines";
import AdminProducts from "@/pages/admin/Products";
import AdminProposals from "@/pages/admin/Proposals";
import AdminProposalPresets from "@/pages/admin/ProposalPresets";
import { AdminCustomersList, AdminCustomerProfile } from "@/pages/admin/Customers";
import AdminMemberships from "@/pages/admin/Memberships";
import AdminTransfers from "@/pages/admin/Transfers";
import SharedPassport from "@/pages/SharedPassport";
import VerifyCertificate from "@/pages/VerifyCertificate";
import TradeBranding from "@/pages/trade/Branding";
import TradeCertificates from "@/pages/trade/Certificates";
import AdminPartners from "@/pages/admin/Partners";
import AdminPWSPartnerNetwork from "@/pages/admin/PWSPartnerNetwork";
import AdminPwsFranchiseeInterest from "@/pages/admin/AdminPwsFranchiseeInterest";
import AdminPWSCommissions from "@/pages/admin/PWSCommissions";
import AdminPWSNotifications from "@/pages/admin/PWSNotifications";
import AdminPWSReporting from "@/pages/admin/PWSReporting";
import AdminPWSSM8Revenue from "@/pages/admin/PWSSM8Revenue";
import AdminPWSLeadValue from "@/pages/admin/PWSLeadValue";
import AdminPWSFollowUps from "@/pages/admin/PWSFollowUps";
import AdminPWSOpportunityWorkflow from "@/pages/admin/PWSOpportunityWorkflow";
import AdminPWSCustomerFollowUps from "@/pages/admin/PWSCustomerFollowUps";
import AdminPartnerCustomers from "@/pages/admin/PartnerCustomers";
import AdminPartnerCustomerDetail from "@/pages/admin/PartnerCustomerDetail";
import AdminPartnerNotifyHealth from "@/pages/admin/PartnerNotifyHealth";
import PostedKitAddressRepairList from "@/pages/admin/PostedKitAddressRepairList";
import AdminPWSMarketingKit from "@/pages/admin/PWSMarketingKit";
import AdminPartnerBroadcast from "@/pages/admin/PartnerBroadcast";
import AdminPWSFieldLabels from "@/pages/admin/PWSFieldLabels";
import AdminPWSPartnerRankings from "@/pages/admin/PWSPartnerRankings";
import AdminPWSTracking from "@/pages/admin/PWSTracking";
import AdminPWSRetestPrompts from "@/pages/admin/PWSRetestPrompts";
import AdminPWSSuccessPack from "@/pages/admin/PWSSuccessPack";
import AdminPWSSampleCollection from "@/pages/admin/PWSSampleCollection";
import AdminPWSWholesaleBundles from "@/pages/admin/PWSWholesaleBundles";
import AdminPWSPayoutRuns from "@/pages/admin/PWSPayoutRuns";
import AdminXeroUploadSettings from "@/pages/admin/AdminXeroUploadSettings";
import AdminPostGridSettings from "@/pages/admin/AdminPostGridSettings";
import AdminBingMailSettings from "@/pages/admin/AdminBingMailSettings";
import AdminServiceM8Settings from "@/pages/admin/AdminServiceM8Settings";
import AdminBulkKitSettings from "@/pages/admin/AdminBulkKitSettings";
import AdminBookingAvailability from "@/pages/admin/AdminBookingAvailability";
import AdminNewBookingNotifications from "@/pages/admin/AdminNewBookingNotifications";
import AdminPwsPartnerLanding from "@/pages/admin/AdminPwsPartnerLanding";
import AdminBulkKitOrders from "@/pages/admin/AdminBulkKitOrders";
import AdminBulkKitPacking from "@/pages/admin/AdminBulkKitPacking";
import AdminKitReimbursements from "@/pages/admin/AdminKitReimbursements";
import AdminKitReimbursementPayoutRuns from "@/pages/admin/AdminKitReimbursementPayoutRuns";
// Feb 2026 — Preview-only PWS Partner customer landing (approval-gated).
import PwsPartnerStorefront from "@/pages/PwsPartnerStorefront";
import ShortPartnerLink from "@/pages/ShortPartnerLink";
import PwsPartnerWaterTestingPreview from "@/pages/preview/PwsPartnerWaterTestingPreview";
import FindPartner from "@/pages/FindPartner";
import AdminPwsPartnerInterest from "@/pages/admin/AdminPwsPartnerInterest";
import AdminPWSPartnerActivityControls from "@/pages/admin/PWSPartnerActivityControls";
import AdminPWSPartnerServiceLevels from "@/pages/admin/PWSPartnerServiceLevels";
import AdminPWSPartnerOverview from "@/pages/admin/PWSPartnerOverview";
import PwsPartnerQrDirectory from "@/pages/admin/PwsPartnerQrDirectory";
import AdminPricingPartnerTypes from "@/pages/admin/PricingPartnerTypes";
import AdminTestCatalogPricing from "@/pages/admin/AdminTestCatalogPricing";
import AdminOnlineDiscountSettings from "@/pages/admin/AdminOnlineDiscountSettings";
import AdminSettingsHub from "@/pages/admin/AdminSettingsHub";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminAnalyticsDashboard from "@/pages/admin/AnalyticsDashboard";
import LabDashboard from "@/pages/lab/LabDashboard";
import BookingDetail from "@/pages/admin/BookingDetail";
import AdminCustomerRequests from "@/pages/admin/CustomerRequests";
import AdminAttribution from "@/pages/admin/Attribution";
import AdminCommissions from "@/pages/admin/Commissions";
import AdminDiscountCodes from "@/pages/admin/DiscountCodes";
import TradeEarnings from "@/pages/trade/Earnings";
import AdminPartnerAcquisition from "@/pages/admin/AdminPartnerAcquisition";
import AdminFeatureFlags from "@/pages/admin/FeatureFlags";
import AdminPostedKitSettings from "@/pages/admin/AdminPostedKitSettings";
import AdminPricingAndFulfilment from "@/pages/admin/AdminPricingAndFulfilment";
import AdminKitsToday from "@/pages/admin/KitsToday";
import AdminTankWaterHealthChecks from "@/pages/admin/TankWaterHealthChecks";
import MarketingCampaignPerformance from "@/pages/admin/marketing/CampaignPerformance";
import MarketingCampaignPerformanceDetail from "@/pages/admin/marketing/CampaignPerformanceDetail";
import MarketingCampaignSettings from "@/pages/admin/marketing/CampaignSettings";
import MarketingAdSpend from "@/pages/admin/marketing/AdSpend";
import AdminAttributionTools from "@/pages/admin/AdminAttributionTools";
import AdminReferralInvites from "@/pages/admin/ReferralInvites";
import AdminQuotes from "@/pages/admin/Quotes";
import QuoteAccept from "@/pages/QuoteAccept";
import PublicProposal from "@/pages/PublicProposal";
import PropertyCheck from "@/pages/PropertyCheck";
import TestOffer from "@/pages/TestOffer";
import FieldInspection from "@/pages/FieldInspection";
import FieldToday from "@/pages/FieldToday";
import FieldHealthCheck from "@/pages/FieldHealthCheck";
import QuickTestBooking from "@/pages/QuickTestBooking";
import FieldLogin, { FieldVerify } from "@/pages/FieldLogin";
import FieldTechnicians from "@/pages/FieldTechnicians";
import PropertyCampaignDetail, { PropertyCampaignsList } from "@/pages/admin/PropertyCampaigns";
import PostcardStudio from "@/pages/admin/PostcardStudio";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import PostcardStudioEditor from "@/pages/admin/PostcardStudioEditor";
import ResponseDashboard from "@/pages/admin/ResponseDashboard";
import AdminAvailability from "@/pages/admin/AdminAvailability";
import AdminGeoRouting from "@/pages/admin/AdminGeoRouting";
import AddressDatabase from "@/pages/admin/AddressDatabase";
import AddressImport from "@/pages/admin/AddressImport";
import AddressGroups from "@/pages/admin/AddressGroups";
import MasterPropertyDetail from "@/pages/admin/MasterPropertyDetail";
import AdminInspectionReview from "@/pages/admin/AdminInspectionReview";
import ProductServicePage from "@/components/products/ProductServicePage";
import ScrollToTop from "@/components/ScrollToTop";
import { PortalShell } from "@/components/PortalShell";
import { useAuth } from "@/lib/auth";

/**
 * Phase 2L-A16 — Admin Dashboard role gate.
 *
 * `/admin/dashboard` is admin-only (rich business analytics). Lab techs who
 * hit this URL get sent to their own task-focused dashboard so they land on
 * something useful instead of a 403 / empty fallback.
 */
/** Retired public route: explicit noindex, then the original redirect. */
function RetiredRoute({ to }) {
  useEffect(() => {
    let m = document.querySelector('meta[name="robots"]');
    if (!m) {
      m = document.createElement("meta");
      m.setAttribute("name", "robots");
      document.head.appendChild(m);
    }
    m.setAttribute("content", "noindex,follow");
    m.setAttribute("data-retired-route", "1");
  }, []);
  return <Navigate to={to} replace />;
}

// Jun 2026 — short vanity URL /check → /tank-water-health-check.
// The query string + hash are carried across so Facebook / letter
// UTMs and fbclid survive the redirect (attribution depends on it).
function CheckShortlinkRedirect() {
  const { search, hash } = window.location;
  return <Navigate to={`/tank-water-health-check${search || ""}${hash || ""}`} replace />;
}


function AdminDashboardGate() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "lab_technician") return <Navigate to="/lab/dashboard" replace />;
  return <ProtectedRoute requireAdmin><AdminAnalyticsDashboard /></ProtectedRoute>;
}

// Jun 2026 — "My Water" portal front door. Logged-in users go to
// their dashboard, everyone else to the customer login.
function MyWaterGate() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return <Navigate to={user ? "/customer" : "/login"} replace />;
}

// Jun 2026 — Unified AWARE login. Legacy gateway URLs (/customer/login,
// /trade/login, /partner/login) redirect here client-side (temporary, not
// a cached 301) preserving the safe `next` + `expired` context so old
// bookmarks, emails and partner links keep working.
function LegacyLoginRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const keep = new URLSearchParams();
  for (const k of ["next", "expired"]) {
    const v = params.get(k);
    if (v) keep.set(k, v);
  }
  const q = keep.toString();
  return <Navigate to={`/login${q ? `?${q}` : ""}`} replace />;
}

function AdminWaterHealthGate() {
  // AdminWaterHealth uses a token-authenticated axios client. Pull the
  // token off the auth context (matches the other admin pages).
  const { user, loading } = useAuth();
  if (loading) return null;
  const token = user?.token || localStorage.getItem("token") || "";
  return <AdminWaterHealth token={token} />;
}

function RouterWithAnalytics() {
  usePageTracking();
  const location = useLocation();
  const navigate = useNavigate();

  // Jun 2026 — home-screen icon fallback. iOS sometimes ignores the
  // per-partner manifest swap and launches saved partner pages at the
  // site-wide start_url ("/"). When launched standalone (from a
  // home-screen icon) directly onto "/", forward to the partner page
  // the visitor saved (recorded by PwsPartnerStorefront).
  useEffect(() => {
    const standalone =
      window.navigator.standalone === true ||
      window.matchMedia?.("(display-mode: standalone)")?.matches;
    if (!standalone || location.pathname !== "/") return;
    let target = null;
    try { target = localStorage.getItem("aware_pwa_target"); } catch { /* ignore */ }
    if (target && target.startsWith("/pws-partner/")) {
      navigate(target, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      {/* SCWT-MAILOUT-DRAFT · Feb 2026 — DRAFT / preview only.
          Not linked from Header nav. Not in sitemap.
          Page itself sets `noindex,nofollow` via useSEO. */}
      <Route path="/mail-out-water-testing-kits" element={<MailOutWaterTestingKits />} />
      <Route path="/book" element={<Booking />} />
      <Route path="/book-water-test" element={<Booking />} />
      <Route path="/book/success" element={<BookingSuccess />} />
      <Route path="/book/cancel" element={<BookingCancel />} />

      {/* Dedicated SEO landing pages for the three core test packages.
          Feb 2026 slug rename — canonical URLs are the AWARE-prefixed
          ones. The three legacy slugs redirect via <Navigate replace>
          so pre-existing Google index entries, printed marketing and
          backlinks continue to work while search engines re-index. */}
      <Route path="/aware-4-point"    element={<BasicWaterSafetyCheck />} />
      <Route path="/aware-tank-360"   element={<EssentialTankWaterTest />} />
      <Route path="/aware-bore-360"   element={<EssentialBoreWaterTest />} />
      <Route path="/bore-water-testing" element={<BoreWaterTestingLanding />} />
      {/* Sep 2026 — paid-social campaign funnel (standalone header/footer,
          noindex: it must not compete with the organic SEO pages). */}
      <Route path="/tank-water-health-check" element={<TankWaterHealthCheck />} />
      <Route path="/tank-water-health-check/confirmed" element={<TankWaterHealthCheckConfirmed />} />
      <Route path="/tank-water-health-check/enquiry-received" element={<TankWaterHealthCheckReferralReceived />} />
      <Route path="/tank-water-health-check/lab-offer" element={<TankWaterHealthCheckLabOffer />} />
      <Route path="/tank-water-health-check/lab-confirmed" element={<TankWaterHealthCheckLabConfirmed />} />
      {/* Personalised Health Check action plan — secure, non-guessable token */}
      <Route path="/r/:token" element={<HealthCheckActionPlan />} />
      <Route path="/health-check/:token" element={<HealthCheckActionPlan />} />
      <Route path="/tank-water-health-check/*" element={<Navigate to="/tank-water-health-check" replace />} />
      <Route path="/check" element={<CheckShortlinkRedirect />} />
      <Route path="/basic-water-safety-check"  element={<Navigate to="/aware-4-point"  replace />} />
      <Route path="/essential-tank-water-test" element={<Navigate to="/aware-tank-360" replace />} />
      <Route path="/essential-bore-water-test" element={<Navigate to="/aware-bore-360" replace />} />

      {/* Audience pages */}
      <Route path="/property-managers" element={<PropertyManagers />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/faqs" element={<FAQs />} />
      {/* Phase 2L-A101 — hidden duplicate homepage for ebook
          lead-magnet review. Intentionally NOT in sitemap and NOT
          linked from any nav. Carries noindex/nofollow at render. */}
      <Route path="/home-ebook-review" element={<HomeEbookReview />} />
      {/* Phase 2L-A102 — dedicated ebook landing page (full copy +
          form). Stays noindex + out of sitemap while in review. */}
      <Route path="/free-guide" element={<FreeGuide />} />

      {/* Friendly aliases for primary nav (point at canonical pages) */}
      <Route path="/service-areas" element={<LocationsHub />} />
      <Route path="/learn" element={<Navigate to="/knowledge-hub" replace />} />

      <Route path="/knowledge-hub" element={<KnowledgeHub />} />
      <Route path="/knowledge-hub/:slug" element={<Article />} />
      <Route path="/invite/:token" element={<InviteAccept />} />
      <Route path="/invite-a-neighbour" element={<InviteNeighbour />} />

      {/* Sep 2026 — `/water-testing` (no suburb) was a hard 404 while
          FOUR internal "Compare test packages" CTAs pointed at it,
          including the 404 page's own suggestion list — so a lost
          visitor clicking the recovery link got a second 404. The
          package comparison lives in the homepage #water-tests section
          (TestPicker + Pricing + TestComparison), so send it there.
          The CTAs now link straight to the destination; this route
          exists for anyone who has the bare URL bookmarked or indexed. */}
      <Route path="/water-testing" element={<Navigate to="/#water-tests" replace />} />
      <Route path="/water-testing/:suburb" element={<SuburbLanding />} />
      {/* Phase 2L-A36 — legacy `/water-testing-{suburb}` dash-form URLs
          (still indexed in Google) redirect to the new
          `/locations/{suburb}-water-testing` slug. The same redirect is
          done by an inline script in public/index.html so the swap fires
          before React mounts; this route is the belt-and-braces fallback
          if the inline script ever fails to execute. */}
      <Route path="/water-testing-:legacySuburb" element={<LegacyWaterTestingRedirect />} />
      <Route path="/locations" element={<LocationsHub />} />
      {/* Feb 2026 · National SEO Architecture pilot — hierarchical
          location URLs. `/locations/:slugOrState` is a shared entry
          point that decides whether the slug is a country/state hub
          (australia / queensland) or a legacy suburb slug
          (e.g. `doonan-water-testing`). More-specific routes with
          nested segments are matched first. */}
      <Route path="/locations/:state/:region/:suburb" element={<HierarchicalSuburbLanding />} />
      <Route path="/locations/:state/:region" element={<RegionHub />} />
      <Route path="/locations/:slug" element={<LocationTopLevelRouter />} />
      <Route path="/about" element={<About />} />
      <Route path="/partner-network" element={<PartnerNetwork />} />
      <Route path="/partners" element={<PartnerProgram />} />
      <Route path="/go/:slug" element={<ReferralLanding />} />
      <Route path="/partner-hub" element={<ProtectedRoute requirePartner><PartnerHub /></ProtectedRoute>} />
      <Route path="/how-local-water-testing-support-works" element={<PartnerNetwork />} />
      <Route path="/pws-franchisee-network" element={<PwsFranchiseeNetwork />} />
      <Route path="/pws-partner-presentation" element={<PwsPartnerPresentation />} />
      {/* Feb 2026 (July) — PWS × AWARE Conference Landing.
          Ordered BEFORE `/pws-partner/:slug` so /pws-partner exact,
          /pws-partner/thank-you and /pws-partner/guide never fall
          through to the per-franchisee landing router. */}
      <Route path="/pws-partner"           element={<PwsPartnerConferenceLanding />} />
      <Route path="/pws-partner/thank-you" element={<PwsPartnerConferenceThankYou />} />
      <Route path="/pws-partner/guide"     element={<PwsPartnerGuideViewer />} />
      {/* Feb 2026 — Per-Franchisee AWARE Landing Page (Phase 1).
          Public URL. Draft/archived/unknown slugs 404. */}
      <Route path="/pws-partner/:slug" element={<PwsPartnerStorefront />} />
      <Route path="/p/:slug" element={<ShortPartnerLink />} />
      {/* Feb 2026 · Find Your Local Water Expert — public suburb/postcode
          → partner storefront. Falls back to national mail-out when no
          active partner matches. Aliases below cover common variants. */}
      <Route path="/find-a-partner" element={<FindPartner />} />
      <Route path="/find-your-local-water-expert" element={<FindPartner />} />
      <Route path="/find-partner" element={<FindPartner />} />
      {/* Jun 2026 — $99/yr membership retired; old links land on My Water.
          Jun 2026 GSC audit: the edge crawler renders this route with JS, so
          the static noindex fallback never reached Google. RetiredRoute
          stamps `noindex,follow` on the document BEFORE redirecting, and
          leaves it in place because this document IS /membership. Routing
          and auth behaviour are unchanged. */}
      <Route path="/membership" element={<RetiredRoute to="/my-water" />} />

      <Route path="/track" element={<Track />} />
      <Route path="/track/:reference" element={<Track />} />

      <Route path="/trade/login" element={<LegacyLoginRedirect />} />
      <Route path="/login" element={<UnifiedLogin />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/auth/complete" element={<AuthComplete />} />
      <Route path="/account/sign-in-methods" element={<ProtectedRoute><AccountSignInMethods /></ProtectedRoute>} />
      <Route path="/welcome" element={<ProtectedRoute><AccountWelcome /></ProtectedRoute>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/trade/register" element={<Register />} />
      <Route path="/trade/pending" element={<Pending />} />

      <Route path="/trade" element={<ProtectedRoute><TradeDashboard /></ProtectedRoute>} />
      <Route path="/trade/order" element={<ProtectedRoute><PlaceOrder /></ProtectedRoute>} />
      <Route path="/trade/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />

      <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
      <Route path="/admin/dashboard" element={<AdminDashboardGate />} />
      <Route path="/lab/dashboard" element={<ProtectedRoute requireLabStaff><LabDashboard /></ProtectedRoute>} />
      <Route path="/admin/bookings/:ref" element={<ProtectedRoute requireLabStaff><BookingDetail /></ProtectedRoute>} />
      <Route path="/admin/customer-requests" element={<ProtectedRoute requireLabStaff><AdminCustomerRequests /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute requireAdmin><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/blog" element={<ProtectedRoute requireAdmin><AdminBlog /></ProtectedRoute>} />
      <Route path="/admin/bookings" element={<ProtectedRoute requireLabStaff><AdminBookings /></ProtectedRoute>} />
      <Route path="/admin/carts" element={<ProtectedRoute requireAdmin><AdminCarts /></ProtectedRoute>} />
      <Route path="/admin/products" element={<ProtectedRoute requireAdmin><AdminProducts /></ProtectedRoute>} />
      <Route path="/admin/proposals" element={<ProtectedRoute requireAdmin><AdminProposals /></ProtectedRoute>} />
      <Route path="/admin/proposal-presets" element={<ProtectedRoute requireAdmin><AdminProposalPresets /></ProtectedRoute>} />
      {/* Phase 2J-AB: support the deep-link shape `/admin/proposals/{id}`
          used by the Estimate Request status card on the lab detail
          page. The Proposals screen itself uses a `?focus=<id>` query
          param to open the proposal drawer, so we redirect once into
          that canonical URL. No new page, no new data model. */}
      <Route
        path="/admin/proposals/:id"
        element={
          <ProtectedRoute requireAdmin>
            <ProposalDeepLink />
          </ProtectedRoute>
        }
      />
      <Route path="/admin/properties" element={<ProtectedRoute requireLabStaff><AdminProperties /></ProtectedRoute>} />
      <Route path="/admin/properties/:id" element={<ProtectedRoute requireLabStaff><AdminPropertyDetail /></ProtectedRoute>} />
      <Route path="/admin/lab" element={<ProtectedRoute requireLabStaff><AdminLab /></ProtectedRoute>} />
      <Route path="/admin/lab/:id" element={<ProtectedRoute requireLabStaff><AdminLabDetail /></ProtectedRoute>} />
      <Route path="/admin/emails" element={<ProtectedRoute requireAdmin><AdminEmails /></ProtectedRoute>} />
      <Route path="/admin/inbound-email/unmatched" element={<ProtectedRoute requireAdmin><AdminUnmatchedEmails /></ProtectedRoute>} />
      <Route path="/admin/readiness" element={<ProtectedRoute requireAdmin><AdminReadiness /></ProtectedRoute>} />
      <Route path="/admin/stripe" element={<ProtectedRoute requireAdmin><AdminStripeLogs /></ProtectedRoute>} />
      <Route path="/admin/lab/bulk" element={<ProtectedRoute requireLabStaff><AdminBulkReceive /></ProtectedRoute>} />
      <Route path="/admin/recommendations" element={<ProtectedRoute requireAdmin><AdminRecommendations /></ProtectedRoute>} />
      <Route path="/admin/agricultural-guidelines" element={<ProtectedRoute requireAdmin><AdminAgriculturalGuidelines /></ProtectedRoute>} />
      <Route path="/admin/customers" element={<ProtectedRoute requireLabStaff><AdminCustomersList /></ProtectedRoute>} />
      <Route path="/admin/customers/:id" element={<ProtectedRoute requireLabStaff><AdminCustomerProfile /></ProtectedRoute>} />
      <Route path="/admin/memberships" element={<ProtectedRoute requireAdmin><AdminMemberships /></ProtectedRoute>} />
      <Route path="/admin/transfers" element={<ProtectedRoute requireAdmin><AdminTransfers /></ProtectedRoute>} />
      <Route path="/shared/passport/:token" element={<SharedPassport />} />
      <Route path="/verify/cert/:certNumber" element={<VerifyCertificate />} />
      <Route path="/trade/branding" element={<ProtectedRoute><TradeBranding /></ProtectedRoute>} />
      <Route path="/trade/certificates" element={<ProtectedRoute><TradeCertificates /></ProtectedRoute>} />
      <Route path="/admin/partners" element={<ProtectedRoute requireAdmin><AdminPartners /></ProtectedRoute>} />
      <Route path="/admin/partner-prospects" element={<ProtectedRoute requireAdmin><PartnerProspects /></ProtectedRoute>} />
      <Route path="/admin/partner-acquisition" element={<ProtectedRoute requireAdmin><AdminPartnerAcquisition /></ProtectedRoute>} />
      <Route path="/admin/retail-invoices" element={<ProtectedRoute requireAdmin><RetailInvoices /></ProtectedRoute>} />
      <Route path="/admin/trade-hub" element={<ProtectedRoute requireAdmin><TradeHub /></ProtectedRoute>} />
      <Route path="/admin/partners/network" element={<ProtectedRoute requireAdmin><AdminPWSPartnerNetwork /></ProtectedRoute>} />
      <Route path="/admin/pws-franchisee-interest" element={<ProtectedRoute requireAdmin><AdminPwsFranchiseeInterest /></ProtectedRoute>} />
      <Route path="/admin/partners/commissions" element={<ProtectedRoute requireAdmin><AdminPWSCommissions /></ProtectedRoute>} />
      <Route path="/admin/partners/notifications" element={<ProtectedRoute requireAdmin><AdminPWSNotifications /></ProtectedRoute>} />
      <Route path="/admin/pws-notifications" element={<ProtectedRoute requireAdmin><AdminPWSNotifications /></ProtectedRoute>} />
      <Route path="/admin/partners/reporting" element={<ProtectedRoute requireAdmin><AdminPWSReporting /></ProtectedRoute>} />
      <Route path="/admin/reports/post-test-service-revenue" element={<ProtectedRoute requireAdmin><AdminPWSSM8Revenue /></ProtectedRoute>} />
      <Route path="/admin/partners/lead-value" element={<ProtectedRoute requireAdmin><AdminPWSLeadValue /></ProtectedRoute>} />
      <Route path="/admin/partners/follow-ups" element={<ProtectedRoute requireAdmin><AdminPWSFollowUps /></ProtectedRoute>} />
      <Route path="/admin/partners/opportunities" element={<ProtectedRoute requireAdmin><AdminPWSOpportunityWorkflow /></ProtectedRoute>} />
      <Route path="/admin/partners/customer-follow-ups" element={<ProtectedRoute requireAdmin><AdminPWSCustomerFollowUps /></ProtectedRoute>} />
      <Route path="/admin/partners/customers" element={<ProtectedRoute requireAdmin><AdminPartnerCustomers /></ProtectedRoute>} />
      <Route path="/admin/partners/customers/:partnerId/:customerKey" element={<ProtectedRoute requireAdmin><AdminPartnerCustomerDetail /></ProtectedRoute>} />
      <Route path="/admin/partner-notify-health" element={<ProtectedRoute requireAdmin><AdminPartnerNotifyHealth /></ProtectedRoute>} />
      <Route path="/admin/posted-kit/needs-address" element={<ProtectedRoute requireAdmin><PostedKitAddressRepairList /></ProtectedRoute>} />
      <Route path="/admin/partners/marketing-kit" element={<ProtectedRoute requireAdmin><AdminPWSMarketingKit /></ProtectedRoute>} />
      <Route path="/admin/partner-broadcast" element={<ProtectedRoute requireAdmin><AdminPartnerBroadcast /></ProtectedRoute>} />
      <Route path="/admin/partners/field-labels" element={<ProtectedRoute requireAdmin><AdminPWSFieldLabels /></ProtectedRoute>} />
      <Route path="/admin/partners/rankings" element={<ProtectedRoute requireAdmin><AdminPWSPartnerRankings /></ProtectedRoute>} />
      <Route path="/admin/partners/tracking" element={<ProtectedRoute requireAdmin><AdminPWSTracking /></ProtectedRoute>} />
      <Route path="/admin/partners/retest-prompts" element={<ProtectedRoute requireAdmin><AdminPWSRetestPrompts /></ProtectedRoute>} />
      <Route path="/admin/partners/success-pack" element={<ProtectedRoute requireAdmin><AdminPWSSuccessPack /></ProtectedRoute>} />
      <Route path="/admin/partners/sample-collection" element={<ProtectedRoute requireAdmin><AdminPWSSampleCollection /></ProtectedRoute>} />
      <Route path="/admin/partners/wholesale-bundles" element={<ProtectedRoute requireAdmin><AdminPWSWholesaleBundles /></ProtectedRoute>} />
      <Route path="/admin/partners/payouts" element={<ProtectedRoute requireAdmin><AdminPWSPayoutRuns /></ProtectedRoute>} />
      <Route path="/admin/partners/activity-controls" element={<ProtectedRoute requireAdmin><AdminPWSPartnerActivityControls /></ProtectedRoute>} />
      <Route path="/admin/partners/service-levels" element={<ProtectedRoute requireAdmin><AdminPWSPartnerServiceLevels /></ProtectedRoute>} />
      {/* Phase 2L-A139 — PWS Partner Overview (Phase 1, read-only) */}
      <Route path="/admin/partners/pws-overview" element={<ProtectedRoute requireAdmin><AdminPWSPartnerOverview /></ProtectedRoute>} />
      <Route path="/admin/partners/pws-qr-directory" element={<ProtectedRoute requireAdmin><PwsPartnerQrDirectory /></ProtectedRoute>} />
      {/* Feb 2026 — Franchisee Presentation deck. The admin route is
          just the same component with `?admin=1` shown by default (via
          location.pathname sniff inside the component), which surfaces
          the "Copy share link" button. Public route is
          `/pws-partner-presentation` above. */}
      <Route path="/admin/partners/pws-presentation" element={<ProtectedRoute requireAdmin><PwsPartnerPresentation /></ProtectedRoute>} />
      {/* Phase 2L-A134 — Partner Pricing Controls v1 (PM + trade partner types) */}
      <Route path="/admin/pricing/partner-types" element={<ProtectedRoute requireAdmin><AdminPricingPartnerTypes /></ProtectedRoute>} />
      <Route path="/admin/water-health" element={<ProtectedRoute requireAdmin><AdminWaterHealthGate /></ProtectedRoute>} />
      {/* SCWT-PM-V2 · Phase 8 · Feb 2026 — PM & PWS Partner Marketing Resources */}
      <Route path="/admin/pm-marketing-resources" element={<ProtectedRoute requireAdmin><AdminPmMarketingResources /></ProtectedRoute>} />
      <Route path="/partner/pm-program-resources" element={<ProtectedRoute><PwsPartnerMarketingResources /></ProtectedRoute>} />
      <Route path="/property-manager/marketing-resources" element={<ProtectedRoute><PmMarketingResources /></ProtectedRoute>} />
      <Route path="/admin/pricing/test-catalog" element={<ProtectedRoute requireAdmin><AdminTestCatalogPricing /></ProtectedRoute>} />
      <Route path="/admin/settings/online-discounts" element={<ProtectedRoute requireAdmin><AdminOnlineDiscountSettings /></ProtectedRoute>} />
      {/* Feb 2026 — central Settings hub. Lists every admin settings +
          pricing page in one place so admins don't need to memorise
          deep URLs. Must be registered AFTER the specific /admin/settings/*
          child routes so React Router matches those first. */}
      <Route path="/admin/settings" element={<ProtectedRoute requireAdmin><AdminSettingsHub /></ProtectedRoute>} />
      <Route path="/admin/attribution" element={<ProtectedRoute requireAdmin><AdminAttribution /></ProtectedRoute>} />
      <Route path="/admin/commissions" element={<ProtectedRoute requireAdmin><AdminCommissions /></ProtectedRoute>} />
      <Route path="/admin/discounts" element={<ProtectedRoute requireAdmin><AdminDiscountCodes /></ProtectedRoute>} />
      <Route path="/admin/settings/features" element={<ProtectedRoute requireAdmin><AdminFeatureFlags /></ProtectedRoute>} />
      <Route path="/admin/settings/posted-kit" element={<ProtectedRoute requireAdmin><AdminPostedKitSettings /></ProtectedRoute>} />
      <Route path="/admin/settings/pricing-and-fulfilment" element={<ProtectedRoute requireAdmin><AdminPricingAndFulfilment /></ProtectedRoute>} />
      <Route path="/admin/settings/xero-upload" element={<ProtectedRoute requireAdmin><AdminXeroUploadSettings /></ProtectedRoute>} />
      <Route path="/admin/settings/postgrid" element={<ProtectedRoute requireAdmin><AdminPostGridSettings /></ProtectedRoute>} />
      <Route path="/admin/settings/bing-mail" element={<ProtectedRoute requireAdmin><AdminBingMailSettings /></ProtectedRoute>} />
      <Route path="/admin/settings/servicem8" element={<ProtectedRoute requireAdmin><AdminServiceM8Settings /></ProtectedRoute>} />
      <Route path="/admin/settings/bulk-kit" element={<ProtectedRoute requireAdmin><AdminBulkKitSettings /></ProtectedRoute>} />
      <Route path="/admin/settings/booking-availability" element={<ProtectedRoute requireAdmin><AdminBookingAvailability /></ProtectedRoute>} />
      <Route path="/admin/settings/new-booking-notifications" element={<ProtectedRoute requireAdmin><AdminNewBookingNotifications /></ProtectedRoute>} />
      {/* Feb 2026 — PWS Partner Landing Pages admin (Phase 1) */}
      <Route path="/admin/pws-partner-landing" element={<ProtectedRoute requireAdmin><AdminPwsPartnerLanding /></ProtectedRoute>} />
      <Route path="/admin/bulk-kit-orders" element={<ProtectedRoute requireAdmin><AdminBulkKitOrders /></ProtectedRoute>} />
      <Route path="/admin/bulk-kit-orders/:orderId/packing" element={<ProtectedRoute requireAdmin><AdminBulkKitPacking /></ProtectedRoute>} />
      <Route path="/admin/kit-reimbursements" element={<ProtectedRoute requireAdmin><AdminKitReimbursements /></ProtectedRoute>} />
      <Route path="/admin/kit-reimbursement-payout-runs" element={<ProtectedRoute requireAdmin><AdminKitReimbursementPayoutRuns /></ProtectedRoute>} />

      {/* Feb 2026 — PREVIEW-ONLY route. Not linked from public nav.
          noindex,nofollow,noarchive,nosnippet applied via useSEO +
          manual robots override. Awaits stakeholder approval before
          promotion to a permanent slug. */}
      <Route path="/preview/pws-partner-water-testing" element={<PwsPartnerWaterTestingPreview />} />
      <Route path="/admin/pws-partner-interest" element={<ProtectedRoute requireAdmin><AdminPwsPartnerInterest /></ProtectedRoute>} />
      <Route path="/admin/kits/today" element={<ProtectedRoute requireAdmin><AdminKitsToday /></ProtectedRoute>} />
      <Route path="/admin/tank-water-health-checks" element={<ProtectedRoute requireAdmin><AdminTankWaterHealthChecks /></ProtectedRoute>} />

      {/* Marketing module (Stage 3) — admin full access, lab_technician read-only */}
      <Route path="/admin/marketing/campaign-performance" element={<ProtectedRoute requireLabStaff><MarketingCampaignPerformance /></ProtectedRoute>} />
      <Route path="/admin/marketing/campaign-performance/:campaignId" element={<ProtectedRoute requireLabStaff><MarketingCampaignPerformanceDetail /></ProtectedRoute>} />
      <Route path="/admin/marketing/campaign-settings" element={<ProtectedRoute requireLabStaff><MarketingCampaignSettings /></ProtectedRoute>} />
      <Route path="/admin/marketing/ad-spend" element={<ProtectedRoute requireLabStaff><MarketingAdSpend /></ProtectedRoute>} />
      <Route path="/admin/tools/attribution" element={<ProtectedRoute requireAdmin><AdminAttributionTools /></ProtectedRoute>} />
      <Route path="/admin/referral-invites" element={<ProtectedRoute requireAdmin><AdminReferralInvites /></ProtectedRoute>} />
      <Route path="/admin/quotes" element={<ProtectedRoute requireAdmin><AdminQuotes /></ProtectedRoute>} />
      <Route path="/quote/:id/accept" element={<QuoteAccept />} />
      <Route path="/proposals/:token" element={<PublicProposal />} />

      {/* Phase 2L-A29 — Product / Service detail pages */}
      <Route path="/products/:slug" element={<ProductOrServicePageRoute kind="products" />} />
      <Route path="/services/:slug" element={<ProductOrServicePageRoute kind="services" />} />

      <Route path="/trade/earnings" element={<ProtectedRoute><TradeEarnings /></ProtectedRoute>} />
      <Route path="/trade/retail" element={<ProtectedRoute><RetailCounter /></ProtectedRoute>} />
      {/* Aug 2026 — duplicate legacy /partners route removed; /partners
          resolves only to PartnerProgram (defined earlier in this file). */}

      {/* Customer (retail) */}
      <Route path="/customer/login" element={<LegacyLoginRedirect />} />
      <Route path="/customer/register" element={<CustomerRegister />} />
      <Route path="/customer" element={<ProtectedRoute><CustomerDashboard /></ProtectedRoute>} />
      <Route path="/my-water" element={<MyWaterGate />} />
      <Route path="/check/:token" element={<PropertyCheck />} />
      <Route path="/test-offer/:token" element={<TestOffer />} />
      <Route path="/field/login" element={<FieldLogin />} />
      <Route path="/field/verify" element={<FieldVerify />} />
      <Route path="/field/technicians" element={<ProtectedRoute fieldLogin><FieldTechnicians /></ProtectedRoute>} />
      <Route path="/field/today" element={<ProtectedRoute fieldLogin><FieldToday /></ProtectedRoute>} />
      <Route path="/field/health-check/:reference" element={<ProtectedRoute fieldLogin fieldRoles={["admin", "lab_technician", "technician"]}><FieldHealthCheck /></ProtectedRoute>} />
      <Route path="/field/quick-booking" element={<ProtectedRoute fieldLogin><QuickTestBooking /></ProtectedRoute>} />
      <Route path="/field/inspection/:appointmentId" element={<ProtectedRoute fieldLogin><FieldInspection /></ProtectedRoute>} />
      {/* Campaign/address admin pages don't render their own shell —
          wrap at route level so the admin nav header is always present. */}
      <Route path="/admin/property-campaigns" element={<ProtectedRoute requireAdmin><PortalShell><PropertyCampaignsList /></PortalShell></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute requireAdmin><PortalShell><AdminAnalytics /></PortalShell></ProtectedRoute>} />
      <Route path="/admin/postcard-studio" element={<ProtectedRoute requireAdmin><PortalShell><PostcardStudio /></PortalShell></ProtectedRoute>} />
      <Route path="/admin/postcard-studio/:vid" element={<ProtectedRoute requireAdmin><PortalShell><PostcardStudioEditor /></PortalShell></ProtectedRoute>} />
      <Route path="/admin/response-dashboard" element={<ProtectedRoute requireAdmin><PortalShell><ResponseDashboard /></PortalShell></ProtectedRoute>} />
      <Route path="/admin/availability" element={<ProtectedRoute requireAdmin><PortalShell><AdminAvailability /></PortalShell></ProtectedRoute>} />
      <Route path="/admin/geo-routing" element={<ProtectedRoute requireAdmin><PortalShell><AdminGeoRouting /></PortalShell></ProtectedRoute>} />
      <Route path="/admin/address-database" element={<ProtectedRoute requireAdmin><PortalShell><AddressDatabase /></PortalShell></ProtectedRoute>} />
      <Route path="/admin/address-database/import" element={<ProtectedRoute requireAdmin><PortalShell><AddressImport /></PortalShell></ProtectedRoute>} />
      <Route path="/admin/address-database/groups" element={<ProtectedRoute requireAdmin><PortalShell><AddressGroups /></PortalShell></ProtectedRoute>} />
      <Route path="/admin/address-database/property/:pid" element={<ProtectedRoute requireAdmin><PortalShell><MasterPropertyDetail /></PortalShell></ProtectedRoute>} />
      <Route path="/admin/property-campaigns/:id" element={<ProtectedRoute requireAdmin><PortalShell><PropertyCampaignDetail /></PortalShell></ProtectedRoute>} />
      <Route path="/admin/property-campaigns/:cid/inspections/:iid/review" element={<ProtectedRoute requireAdmin><AdminInspectionReview /></ProtectedRoute>} />

      {/* AWARE Water Health Record */}
      <Route path="/passport" element={<ProtectedRoute><PassportDashboard /></ProtectedRoute>} />
      <Route path="/passport/property/:id" element={<ProtectedRoute><PassportProperty /></ProtectedRoute>} />
      <Route path="/property-manager" element={<ProtectedRoute><PropertyManagerDashboard /></ProtectedRoute>} />
      <Route path="/property-manager/team" element={<ProtectedRoute><PropertyManagerTeam /></ProtectedRoute>} />
      <Route path="/property-manager/invoices" element={<ProtectedRoute><PropertyManagerInvoices /></ProtectedRoute>} />
      <Route path="/property-managers/apply" element={<PropertyManagerApply />} />
      {/* SCWT-PM-V2 Phase 3 — public single-use invite acceptance */}
      <Route path="/pm-invite/:token" element={<PmInviteAccept />} />
      {/* SCWT-PM-V2 Phase 4 — public landlord/tenant pay-for-water-test landing */}
      <Route path="/pay-for-water-test/:token" element={<PayForWaterTest />} />
      {/* SCWT-PM-V2 Phase 5 — Landlord Portal */}
      <Route path="/landlord/login" element={<LandlordLogin />} />
      <Route path="/landlord/verify/:token" element={<LandlordVerify />} />
      <Route path="/landlord" element={<LandlordDashboard />} />
      <Route path="/landlord/water-health/enrol" element={<LandlordWaterHealthEnrol />} />
      <Route path="/landlord/water-health/success" element={<LandlordWaterHealthSuccess />} />
      <Route path="/landlord/water-health/invitations/:token" element={<LandlordWaterHealthInvitation />} />
      <Route path="/admin/partners/applications" element={<ProtectedRoute requireAdmin><AdminPropertyManagerApplications /></ProtectedRoute>} />

      {/* Aware Partner Network — Phase 5: PWS franchisee dashboard */}
      <Route path="/partner/login" element={<LegacyLoginRedirect />} />
      <Route path="/partner" element={<Navigate to="/partner/dashboard" replace />} />
      <Route path="/partner/welcome" element={<ProtectedRoute requirePartner><PartnerWelcome /></ProtectedRoute>} />
      <Route path="/partner/territory" element={<ProtectedRoute requirePartner><PartnerTerritory /></ProtectedRoute>} />
      <Route path="/partner/business-details" element={<ProtectedRoute requirePartner><PartnerBusinessDetails /></ProtectedRoute>} />
      <Route path="/partner/payment-details" element={<ProtectedRoute requirePartner><PartnerPaymentDetails /></ProtectedRoute>} />
      <Route path="/partner/leads" element={<ProtectedRoute requirePartner><PartnerQuoteLeads /></ProtectedRoute>} />
      <Route path="/partner/leads/:leadId" element={<ProtectedRoute requirePartner><PartnerQuoteLeads /></ProtectedRoute>} />
      <Route path="/partner/corrective-leads" element={<ProtectedRoute requirePartner><PartnerCorrectiveLeads /></ProtectedRoute>} />
      <Route path="/partner/marketing-resources" element={<ProtectedRoute requirePartner><PartnerMarketingResources /></ProtectedRoute>} />
      <Route path="/partner/kit-orders" element={<ProtectedRoute requirePartner><PartnerKitOrders /></ProtectedRoute>} />
      <Route path="/partner/documents"  element={<ProtectedRoute requirePartner><PartnerDocuments /></ProtectedRoute>} />
      <Route path="/admin/quote-leads" element={<ProtectedRoute requireAdmin><AdminQuoteLeads /></ProtectedRoute>} />
      <Route path="/partner/dashboard" element={<ProtectedRoute requirePartner><PartnerDashboard /></ProtectedRoute>} />
      <Route path="/partner/profile" element={<ProtectedRoute requirePartner><PartnerProfile /></ProtectedRoute>} />
      <Route path="/partner/guide" element={<ProtectedRoute requirePartner><PartnerGuide /></ProtectedRoute>} />
      <Route path="/partner/ledger" element={<ProtectedRoute requirePartner><PartnerLedger /></ProtectedRoute>} />
      <Route path="/partner/bookings" element={<ProtectedRoute requirePartner><PartnerBookings /></ProtectedRoute>} />
      <Route path="/partner/bookings/:reference" element={<ProtectedRoute requirePartner><PartnerBookingDetail /></ProtectedRoute>} />
      <Route path="/partner/customers" element={<ProtectedRoute requirePartner><PartnerCustomers /></ProtectedRoute>} />
      <Route path="/partner/customers/:customerKey" element={<ProtectedRoute requirePartner><PartnerCustomerDetail /></ProtectedRoute>} />
      <Route path="/partner/passports" element={<ProtectedRoute requirePartner><PartnerPassports /></ProtectedRoute>} />
      <Route path="/partner/passports/:propertyId" element={<ProtectedRoute requirePartner><PartnerPassportDetail /></ProtectedRoute>} />
      <Route path="/partner/reports" element={<ProtectedRoute requirePartner><PartnerReports /></ProtectedRoute>} />
      <Route path="/partner/test-hub" element={<ProtectedRoute requirePartner><PartnerTestHub /></ProtectedRoute>} />
      {/* SCWT-PM-V2 Phase 3 — PWS Partner side invite panel */}
      <Route path="/partner/property-managers" element={<ProtectedRoute requirePartner><PartnerPropertyManagers /></ProtectedRoute>} />

      {/* Sep 2026 — catch-all. Without this an unmatched URL rendered a
          BLANK page (how the partner bell's bad link surfaced). Keep it
          last so it never shadows a real route. */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function ProposalDeepLink() {
  // Phase 2J-AB: redirect `/admin/proposals/{id}` → `/admin/proposals?focus={id}`
  // The Proposals screen uses `?focus=<id>` to open the proposal drawer
  // (read in `Proposals.jsx` via `useSearchParams().get("focus")`). This
  // adapter keeps the deep-link shape that admin emails / lab cards use
  // working without introducing a new page or data model.
  const { id } = useParams();
  return <Navigate to={`/admin/proposals?focus=${id}`} replace />;
}

function ProductOrServicePageRoute({ kind }) {
  // Phase 2L-A29: thin adapter — react-router has already matched
  // `/products/:slug` or `/services/:slug`; we just reassemble the
  // slugPath (e.g. "products/tr20uv") for the content lookup.
  const { slug } = useParams();
  return <ProductServicePage slugPath={`${kind}/${slug}`} />;
}

function LegacyWaterTestingRedirect() {
  // Phase 2L-A36 — fallback for the legacy `/water-testing-{suburb}` URLs
  // still indexed in Google. The primary redirect lives as an inline
  // script in public/index.html (runs before React mounts) — this is
  // the SPA-level safety net in case that script ever fails to fire.
  const { legacySuburb } = useParams();
  const search = useLocation().search || "";
  const slug = (legacySuburb || "").toLowerCase();
  return <Navigate to={`/locations/${slug}-water-testing${search}`} replace />;
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <ScrollToTop />
            <RouterWithAnalytics />
          </BrowserRouter>
          <Toaster position="top-center" richColors />
        </div>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
